import { isEqual } from "lodash";
import { TinyMCE } from "tinymce";

import { convertHtmlToText, getParsedJson } from "utils/common";

export interface ParsedWordProcessorValue {
  original: string;
  html: string;
  text: string;
  hasJson: boolean;
  isTextEmpty: boolean;
}

export const emptyWordProcessorValue = "<p></p>";

export const SPEECH_TO_TEXT_SILENCE_DETECTION_SETTINGS = {
  silence_detection_sec: 300,
  silence_grace_period_sec: 60,
  aggressiveness: 3,
  speech_ratio_threshold: 0.1,
  energy_margin_db: 8.0,
  noise_floor_percentile: 15.0
};

export const cleanDesignerEmailHtmlPreview = (
  html: string | undefined | null
) => {
  if (!html) return "";

  const bodyRegex = /<body[^>]*>([\s\S]*?)<\/body>/i;
  const match = html.match(bodyRegex);

  if (match && match[1]) {
    return match[1].trim();
  }

  return html;
};

export const getParsedWordProcessorValue = (
  initialText: string | undefined,
  normalizeHtml: boolean = false
) => {
  let html = "";
  let text = "";
  let hasJson = false;
  const parsed = getParsedJson(initialText);

  if (parsed && parsed.html) {
    html = parsed.html;
    text = convertHtmlToText(html).trim();
    hasJson = true;
  } else {
    html = initialText ?? "";
    text = convertHtmlToText(html).trim();
  }

  if (normalizeHtml) {
    html = getNormalizedWordProcessorHtml(html);
  }
  return {
    original: initialText,
    html,
    text,
    hasJson,
    isTextEmpty: text.trim() === ""
  } as ParsedWordProcessorValue;
};

export const getNormalizedWordProcessorHtml = (html: string) => {
  if (html && window.tinymce) {
    const node = window?.tinymce.html.DomParser({ validate: true }).parse(html);
    html = window?.tinymce.html
      .Serializer()
      .serialize(node)
      .replaceAll("\n", "")
      .trim();
  }
  return html;
};

export const getTextLength = (content: string, substractMergeTags: boolean) => {
  let length = content.length;

  if (!substractMergeTags) {
    return length;
  }

  let totalCharacters = 0;
  const matches = content.matchAll(/({{.*?}})/g);
  for (const match of matches) {
    totalCharacters += match[1].length;
  }

  return length - totalCharacters;
};

export const isWordProcessorHtmlEqual = (aHtml: string, bHtml: string) =>
  isEqual(
    getNormalizedWordProcessorHtml(aHtml),
    getNormalizedWordProcessorHtml(bHtml)
  ) ||
  (bHtml === "" &&
    getNormalizedWordProcessorHtml(aHtml) ===
      getNormalizedWordProcessorHtml(emptyWordProcessorValue));

export const getWordProcessorInitialText = (text: string | undefined) => {
  const parsed = getParsedWordProcessorValue(text, true);
  return parsed.html;
};

export const getHtmlLikeText = (html: string | null | undefined) => {
  const text = convertHtmlToText(prepareHtmlForTextConversion(html)).replaceAll(
    "\n",
    "<br>"
  );

  return `<div>${text}</div>`;
};

const BLOCK_CLOSE_TAG_REGEX = /<\/(?:p|div|h[1-6]|li|blockquote|pre)>/gi;

const prepareHtmlForTextConversion = (html: string | null | undefined) => {
  return String(html ?? "")
    .replaceAll("\r\n", "\n")
    .replace(/>\s*\n\s*</g, "><")
    .replace(BLOCK_CLOSE_TAG_REGEX, (tag) => `${tag}\n`);
};

declare global {
  interface Window {
    tinymce?: TinyMCE;
  }
}
